import { FlexBox, Text } from '@fable/components'
import { css } from '@fable/theme'
import { useReadingGoals } from '../../lib/ctx/reading_goals'
import { ReadingGoalPeriodType } from '../../lib/types'
import { useStatsPermission } from '../../lib/hooks'
import { isStatsSelfView } from '../../lib/utils'
import { useSearchParams } from 'react-router-dom'
import { useStatsTheme } from '../../theme/ctx'
import { useStats } from '../../lib/ctx/stats'

interface ReadingGoalsTab {
  id: ReadingGoalPeriodType
  label: string
  locked?: boolean
}

export const ReadingGoalsTabs = () => {
  const [
    period,
    setPeriod,
    getReadingGoals,
    getSuggestions,
    status,
    loadingSuggestions,
  ] = useReadingGoals((s) => [
    s.period,
    s.setPeriod,
    s.getReadingGoals,
    s.getSuggestions,
    s.status,
    s.loadingSuggestions,
  ])
  const isWeb = useStats((s) => s.isWeb)
  const [searchParams, setSearchParams] = useSearchParams()
  const { isPlusUser } = useStatsPermission()
  const theme = useStatsTheme((s) => s.theme)

  const tabs: ReadingGoalsTab[] = [
    {
      id: 'year',
      label: 'Yearly',
    },
    {
      id: 'month',
      label: 'Monthly',
      locked: !isPlusUser,
    },
    {
      id: 'week',
      label: 'Weekly',
      locked: !isPlusUser,
    },
  ]

  const tabIndex = tabs.findIndex((tab) => tab.id === period)

  const handleOnClick = async (tab: ReadingGoalsTab) => {
    if (period === tab.id || status === 'loading' || loadingSuggestions) return

    setPeriod(tab.id)

    if (!isWeb) {
      searchParams.set('period_type', tab.id)
      setSearchParams(searchParams)
    }

    if (tab.locked) {
      await getSuggestions(tab.id)
      return
    }
    await getReadingGoals({ period: tab.id, fetchSuggestions: isStatsSelfView })
  }

  return (
    <FlexBox
      className={css`
        position: relative;
        padding: 2px;
        width: 100%;
        height: 36px;
        background-color: ${theme.colors.section.tabBg};
        border-radius: 100px;
        overflow: hidden;
      `}
    >
      <span
        className={css`
          display: flex;
          padding: 2px;
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
        `}
      >
        <span
          className={css`
            position: absolute;
            top: 2px;
            left: 2px;
            width: calc((100% - 4px) / ${tabs.length});
            height: calc(100% - 4px);
            background-color: ${theme.colors.section.tabBtnBg};
            border-radius: 100px;
            transform: translateX(${tabIndex * 100}%);
            transition: transform 0.2s ease-in-out;
            will-change: transform;
          `}
        />
      </span>
      {tabs.map((tab) => (
        <FlexBox
          key={tab.id}
          role="button"
          aria-label={`${tab.id} tab`}
          className={css`
            width: 100%;
            border-radius: 100px;
            cursor: pointer;
            gap: 4px;
            z-index: 1;
            background-color: transparent;
            -webkit-tap-highlight-color: transparent;
            &:active {
              background-color: transparent;
            }
          `}
          onClick={() => handleOnClick(tab)}
          centerAll
        >
          <Text
            className={css`
              font-size: 10px;
              line-height: 10px;
              transition: color 0.2s ease-in-out;
            `}
            type="body"
            weight="600"
            color={
              period === tab.id
                ? theme.colors.section.tabBtnText.active
                : theme.colors.section.tabBtnText.idle
            }
          >
            {tab.label}
          </Text>
        </FlexBox>
      ))}
    </FlexBox>
  )
}
